import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarCheck, CalendarDays, FileText, History, Smartphone, Play, ClipboardCheck, Zap } from "lucide-react";
import { api, errMsg, fmtDate, label } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { LIVE_STATUSES } from "../../lib/portals";
import { Panel, MatchRow, Empty, Spinner, Crest, StatusBadge, DemoTag, IconBox } from "../../components/kit";
import { MatchDayCountdown } from "../../components/MatchDayCountdown";
import { PRE } from "./MatchRoom";

const DONE = ["full_time", "reports_pending", "reports_submitted", "under_review", "validated", "archived", "abandoned"];
const sameDay = (iso) => new Date(iso).toDateString() === new Date().toDateString();

export function useRefMatches() {
  const { me } = useAuth();
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data.filter((m) => m.officials?.referee === me.user.id || Object.values(m.officials || {}).includes(me.user.id)))).catch(() => setList([]));
  }, [me.user.id]);
  return list;
}

export const pickCurrent = (list, uid) => {
  const mine = (list || []).filter((m) => m.officials?.referee === uid);
  return mine.find((m) => LIVE_STATUSES.includes(m.status)) || [...mine].filter((m) => PRE.includes(m.status)).sort((a, b) => a.kickoff.localeCompare(b.kickoff))[0] || null;
};

export const Stat = ({ icon, l, v, to, testid }) => (
  <Link to={to} data-testid={testid} className="tro-card flex items-center gap-3 p-3 transition-colors hover:border-[#C5CBD2]">
    <IconBox icon={icon} size="sm" /><div className="min-w-0"><div className="text-2xl font-extrabold leading-none">{v}</div><div className="mt-1 text-[10px] font-bold uppercase leading-tight tracking-wider text-[#6C757D]">{l}</div></div>
  </Link>
);

function Featured({ m, uid }) {
  const nav = useNavigate();
  const live = LIVE_STATUSES.includes(m.status);
  const role = Object.entries(m.officials || {}).find(([, v]) => v === uid)?.[0];
  const go = `/referee/matches/${m.id}/tactical`;
  return (
    <section className="tro-card relative overflow-hidden" data-testid="featured-match-card">
      <div className="absolute inset-0 tro-metal opacity-60" />
      <div className="relative p-4 md:p-6">
        <div className="flex flex-wrap items-center gap-2"><span className="tro-label">{live ? "Live now" : "Next appointment"}</span><StatusBadge status={m.status} />{m.demo && <DemoTag />}</div>
        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="flex flex-col items-center gap-2 text-center"><Crest club={m.home} size={56} /><b className="text-sm md:text-lg">{m.home.name}</b></div>
          <div className="text-center"><div className="font-mono text-3xl font-extrabold md:text-4xl">{m.score.home}–{m.score.away}</div><div className="text-[11px] font-bold uppercase text-[#6C757D]">{live ? label(m.status) : fmtDate(m.kickoff)}</div></div>
          <div className="flex flex-col items-center gap-2 text-center"><Crest club={m.away} size={56} /><b className="text-sm md:text-lg">{m.away.name}</b></div>
        </div>
        <div className="mt-3 text-center text-xs text-[#343A40]" data-testid="featured-meta">{m.competition?.name} · {m.venue} · Role: <b>{label(role || "referee")}</b></div>
        <button data-testid={live ? "resume-live-match-btn" : "enter-match-btn"} onClick={() => nav(go)}
          className={`mt-4 flex min-h-[60px] w-full items-center justify-center gap-2 rounded-[14px] text-base font-extrabold uppercase tracking-wide text-white transition-transform active:scale-[0.98] ${live ? "bg-[#F02030]" : "bg-[#171B20]"}`}>
          {live ? <><Play className="h-5 w-5" />Resume live match</> : <>Enter match<ArrowRight className="h-5 w-5" /></>}
        </button>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {[["Prepare Match", `${go}?panel=prep`, ClipboardCheck, "prepare-match-btn"], ["View Appointment", "/referee/appointments", CalendarCheck, "view-appointment-btn"], ["Test Device", "/referee/devices", Smartphone, "test-device-btn"]].map(([l, to, I, t]) => (
            <Link key={t} to={to} data-testid={t} className="flex min-h-[48px] flex-col items-center justify-center gap-0.5 rounded-[14px] border border-[#E7EAEE] bg-white px-1 text-center text-[11px] font-bold"><I className="h-4 w-4" />{l}</Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function RefereeDashboard() {
  const { me } = useAuth();
  const [d, setD] = useState(null);
  const [devices, setDevices] = useState(null);
  const [err, setErr] = useState("");
  const list = useRefMatches();
  useEffect(() => {
    api.get("/dashboard/referee").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
    api.get("/devices").then((r) => setDevices(r.data)).catch(() => setDevices([]));
  }, []);
  if (err) return <Empty text={err} />;
  if (!d || !list) return <Spinner />;
  const cur = pickCurrent(list, me.user.id);
  const upcoming = list.filter((m) => PRE.includes(m.status) || LIVE_STATUSES.includes(m.status));
  const today = upcoming.filter((m) => sameDay(m.kickoff) || LIVE_STATUSES.includes(m.status));
  const recent = list.filter((m) => DONE.includes(m.status)).sort((a, b) => b.kickoff.localeCompare(a.kickoff)).slice(0, 4);
  const pending = list.filter((m) => m.officials?.referee === me.user.id && ["full_time", "reports_pending"].includes(m.status));
  const dev = devices?.[0];
  const mem = me.memberships.find((x) => x.status === "active" && x.org_name);
  return (
    <div className="space-y-4 md:space-y-6" data-testid="referee-dashboard">
      <div className="flex items-center justify-between gap-3 tro-fade">
        <div className="min-w-0"><h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">Referee Dashboard</h1><p className="truncate text-sm text-[#6C757D]">{me.user.name}{mem ? ` · ${mem.org_name}` : ""}</p></div>
        <span data-testid="dashboard-device-status" className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${dev ? "bg-[#171B20] text-white" : "bg-[#E7EAEE] text-[#6C757D]"}`}><Smartphone className="h-4 w-4" />{dev ? label(dev.status || "paired") : "No device"}</span>
      </div>
      <div className="grid gap-4 md:gap-6 xl:grid-cols-[1.3fr_1fr]">
        <div className="space-y-4">
          {cur ? <Featured m={cur} uid={me.user.id} /> : <Empty text="No current or upcoming appointment." />}
          {d.next_prematch && <MatchDayCountdown match={d.next_prematch} />}
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2.5 tro-stagger" data-testid="referee-dashboard-cards">
            <Stat icon={Zap} l="Today's Matches" v={today.length} to="/referee/matches" testid="card-today" />
            <Stat icon={CalendarDays} l="Upcoming Appointments" v={upcoming.length} to="/referee/appointments" testid="card-upcoming" />
            <Stat icon={FileText} l="Pending Reports" v={pending.length} to="/referee/reports" testid="card-pending-reports" />
            <Stat icon={History} l="Recent Matches" v={recent.length} to="/referee/matches" testid="card-recent" />
          </div>
          <Panel title="Pending tasks" testid="pending-tasks">
            {d.tasks.length ? <ul className="space-y-1.5 text-sm">{d.tasks.map((t) => <li key={t} className="rounded-[12px] bg-[#F7F8FA] px-3 py-2 font-semibold">{t}</li>)}</ul> : <Empty text="All caught up." />}
          </Panel>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-2">
        <Panel title="Today's matches" testid="today-matches">{today.length ? <div className="space-y-2">{today.map((m) => <MatchRow key={m.id} m={m} to={`/referee/matches/${m.id}/tactical`} />)}</div> : <Empty text="No matches today." />}</Panel>
        <Panel title="Upcoming appointments" testid="upcoming-matches">{upcoming.length ? <div className="space-y-2">{upcoming.slice(0, 5).map((m) => <MatchRow key={m.id} m={m} to={`/referee/matches/${m.id}/tactical`} />)}</div> : <Empty text="No upcoming appointments." />}</Panel>
        <Panel title="Recent matches" testid="recent-matches">{recent.length ? <div className="space-y-2">{recent.map((m) => <MatchRow key={m.id} m={m} />)}</div> : <Empty text="No completed matches yet." />}</Panel>
        <Panel title="Device status" testid="device-status-panel">
          {dev ? <div className="flex items-center gap-3 text-sm"><IconBox icon={Smartphone} /><div><b>{dev.name || dev.serial || "TRO Referee One"}</b><div className="text-xs text-[#6C757D]">{label(dev.status || "paired")} · Simulated rear LED · Battery {dev.battery ?? "—"}%</div></div><Link to="/referee/devices" className="ml-auto text-xs font-bold underline">Manage</Link></div> : <Empty text="No TRO device paired. Card presentation uses this phone's full screen." />}
        </Panel>
      </div>
    </div>
  );
}

export function TacticalEntry() {
  const { me } = useAuth();
  const loc = useLocation();
  const list = useRefMatches();
  const nav = useNavigate();
  const cur = list && pickCurrent(list, me.user.id);
  useEffect(() => { if (cur) nav(`/referee/matches/${cur.id}/tactical${loc.search}`, { replace: true }); }, [cur, nav, loc.search]);
  if (!list || cur) return <Spinner />;
  return <Empty testid="no-tactical-match" text="You have no live or upcoming match as centre referee." />;
}
